import { FLEET_VIOLATION_ACTION_LABELS, type FleetPortalVehicle } from "@station/contracts";
import { Alert, DataTable, type DataTableColumn, EmptyState, StatusBadge } from "@station/ui";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Car } from "lucide-react";
import { type ReactElement, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { formatPlateDisplay } from "../../features/customers/personal-data-values.js";
import { litresText, moneyText } from "../../features/fleet/fleet-values.js";
import { timeWindowLabel } from "../../features/fleet/policy-values.js";
import { usePortal } from "../../features/portal/portal-context.js";
import { usePortalVehicles } from "../../features/portal/queries.js";
import { namesText } from "../../features/portal/portal-values.js";
import { PortalHeader } from "../../features/portal/shared.js";
import { errorMessage } from "../../lib/errors.js";
import { formatCount } from "../../lib/format.js";

export const Route = createFileRoute("/portal/araclar")({ component: PortalVehiclesRoute });

/**
 * Filonun araçları ve ETKİN kuralları (limitler, izinli ürün / istasyon ADLARI, saat pencereleri, km / kart zorunluluğu,
 * ihlal eylemi).
 */
function PortalVehiclesRoute(): ReactElement {
  const { t } = useTranslation();
  const portal = usePortal();
  const vehicles = usePortalVehicles(portal.counterpartyId);

  const columns = useMemo<DataTableColumn<FleetPortalVehicle>[]>(
    () => [
      {
        id: "plate",
        header: t("portal.fields.plate"),
        hideable: false,
        cell: (row) => (
          <Link
            to="/portal/islemler"
            search={{ vehicle: row.vehicleId }}
            className="font-mono whitespace-nowrap underline underline-offset-4"
          >
            {formatPlateDisplay(row.plate)}
            <span className="sr-only"> — {t("portal.vehicles.transactions")}</span>
          </Link>
        ),
      },
      {
        id: "vehicle",
        header: t("portal.fields.vehicle"),
        cell: (row) => (
          <span className="grid gap-0.5">
            <span>{[row.brand, row.model].filter(Boolean).join(" ") || "—"}</span>
            <span className="text-xs text-muted-foreground">{row.fuelProductName ?? ""}</span>
          </span>
        ),
      },
      {
        id: "limits",
        header: t("portal.fields.limits"),
        cell: (row) => {
          const parts = [
            row.policy.dailyLitreLimit
              ? t("portal.vehicles.daily", { value: litresText(row.policy.dailyLitreLimit) })
              : null,
            row.policy.monthlyLitreLimit
              ? t("portal.vehicles.monthly", { value: litresText(row.policy.monthlyLitreLimit) })
              : null,
            row.policy.dailyAmountLimit
              ? t("portal.vehicles.daily", { value: moneyText(row.policy.dailyAmountLimit) })
              : null,
            row.policy.monthlyAmountLimit
              ? t("portal.vehicles.monthly", { value: moneyText(row.policy.monthlyAmountLimit) })
              : null,
          ].filter((part): part is string => part !== null);
          return parts.length === 0 ? (
            <span className="text-muted-foreground">{t("portal.noLimit")}</span>
          ) : (
            <span className="grid gap-0.5 text-sm">
              {parts.map((part) => (
                <span key={part}>{part}</span>
              ))}
            </span>
          );
        },
      },
      {
        id: "allowed",
        header: t("portal.fields.allowed"),
        cell: (row) => (
          <span className="grid gap-0.5 text-sm">
            <span>
              {row.policy.allowedProducts === null
                ? t("portal.vehicles.allProducts")
                : t("portal.vehicles.products", {
                    names: namesText(row.policy.allowedProducts) ?? t("portal.vehicles.none"),
                  })}
            </span>
            <span className="text-muted-foreground">
              {row.policy.allowedStations === null
                ? t("portal.vehicles.allStations")
                : t("portal.vehicles.stations", {
                    names: namesText(row.policy.allowedStations) ?? t("portal.vehicles.none"),
                  })}
            </span>
          </span>
        ),
      },
      {
        id: "windows",
        header: t("portal.fields.hours"),
        cell: (row) =>
          row.policy.timeWindows === null || row.policy.timeWindows.length === 0 ? (
            <span className="text-muted-foreground">{t("portal.vehicles.anyTime")}</span>
          ) : (
            <span className="grid gap-0.5 text-sm">
              {row.policy.timeWindows.map((window) => (
                <span key={`${window.weekdays.join("")}-${window.from}-${window.to}`}>
                  {timeWindowLabel(window, t("portal.vehicles.everyDay"), t("portal.vehicles.allDay"))}
                </span>
              ))}
            </span>
          ),
      },
      {
        id: "requirements",
        header: t("portal.fields.requirements"),
        cell: (row) => (
          <span className="flex flex-wrap gap-1">
            {row.policy.requireOdometer ? (
              <StatusBadge tone="info">{t("portal.vehicles.odometer")}</StatusBadge>
            ) : null}
            {row.policy.requireDriverCard ? (
              <StatusBadge tone="info">{t("portal.vehicles.driverCard")}</StatusBadge>
            ) : null}
            <span className="text-xs text-muted-foreground">
              {t("portal.vehicles.onViolation", {
                action: FLEET_VIOLATION_ACTION_LABELS[row.policy.violationAction],
              })}
            </span>
          </span>
        ),
      },
      {
        id: "odometer",
        header: t("portal.fields.lastOdometer"),
        align: "right",
        cell: (row) =>
          row.lastOdometerKm === null ? (
            "—"
          ) : (
            <span className="tabular">{formatCount(row.lastOdometerKm)} km</span>
          ),
      },
      {
        id: "status",
        header: t("portal.fields.status"),
        cell: (row) => (
          <StatusBadge tone={row.status === "ACTIVE" ? "success" : "neutral"}>
            {row.status === "ACTIVE" ? t("common.active") : t("common.inactive")}
          </StatusBadge>
        ),
      },
    ],
    [t],
  );

  return (
    <>
      <PortalHeader title={t("portal.vehicles.title")} description={t("portal.vehicles.description")} />
      <DataTable
        caption={t("portal.vehicles.title")}
        columns={columns}
        rows={vehicles.data ?? []}
        rowKey={(row) => row.vehicleId}
        loading={vehicles.isPending}
        error={vehicles.error ? <Alert tone="danger">{errorMessage(vehicles.error)}</Alert> : undefined}
        emptyState={
          <EmptyState
            headingLevel={2}
            icon={<Car />}
            title={t("portal.vehicles.emptyTitle")}
            description={t("portal.vehicles.emptyDescription")}
          />
        }
      />
    </>
  );
}
